import BuildIcon from "@mui/icons-material/Build";
import * as React from "react";

import type { ChatToolUseContent } from "@/shared/domain/chat/ChatRecord";
import type { ClientTool } from "@/shared/domain/chat/clientTools";
import { Button } from "@/shared/foundation/primitives";

import styles from "./ToolApprovalCard.module.css";
import { ToolApprovalDetailsModal } from "./ToolApprovalDetailsModal";

interface ToolApprovalCardProps {
  toolUse: ChatToolUseContent;
  tool: ClientTool;
  onApprove: () => void;
  onDecline: () => void;
}

/**
 * Inline approval card rendered in the chat message stream when a
 * client-side tool requires user confirmation before executing.
 *
 * Shows the tool name with Approve/Decline actions. If the tool
 * provides a `ConfirmationContent` component, a "Details" link
 * opens a modal with a richer preview.
 */
export function ToolApprovalCard({
  toolUse,
  tool,
  onApprove,
  onDecline,
}: ToolApprovalCardProps) {
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const cardRef = React.useRef<HTMLDivElement>(null);

  // Scroll the card into view when it first appears. Use block: "start"
  // so the card appears at the top of the viewport, above the scroll-down
  // button which sits at the bottom of the scroll container.
  React.useEffect(() => {
    cardRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, []);

  const hasDetails = !!tool.ConfirmationContent;

  return (
    <div className={styles.container} ref={cardRef}>
      <div className={styles.card}>
        <div className={styles.header}>
          <BuildIcon className={styles.titleIcon} />
          <span className={styles.titleText}>
            <b>{tool.displayName}</b> wants to run
          </span>
        </div>

        {hasDetails && (
          <button
            type="button"
            className={styles.detailsLink}
            onClick={() => setDetailsOpen(true)}
          >
            More details
          </button>
        )}

        <div className={styles.actions}>
          <Button variant="outlined" size="small" onClick={onDecline}>
            Decline
          </Button>
          <Button variant="contained" size="small" onClick={onApprove}>
            Approve
          </Button>
        </div>
      </div>

      {hasDetails && (
        <ToolApprovalDetailsModal
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          toolUse={toolUse}
          tool={tool}
          onApprove={onApprove}
          onDecline={onDecline}
        />
      )}
    </div>
  );
}
